import Typography from "@components_v2/typography/typography";
import MetadataFieldReadMode from "@features/metadata-fields/metadata-field-read-mode";
import { Box, Button, Divider, Grid, List, ListItem, ListItemText, Stack, PDCAlpha as alpha } from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import useUserDetail from "@hooks/useUserDetail";
import UserDetailPopper, { UserDetailButton } from "src/features/user-detail-popper/user-detail-popper";
import { IconAvata } from "@mzc-cloudplex/icons";
import CollectionTypeFlag from "@components_v2/icon-type/asset/collection-type-flag";

const PanelLibraryOverview = ({ id, data }) => {
    const { t } = useTranslation();

    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();

    const getComponentByType = ({ type, id, subtitle, value }) => {
        if (!type) return null;

        if (type === "text") {
            return (
                <>
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                                    {t(`common::label::${subtitle}`, subtitle)}
                                </Typography>
                            </>
                        }
                        secondary={
                            <Grid item sx={{ mt: 1 }}>
                                <Typography
                                    variant={"body1_400"}
                                    component={"span"}
                                    sx={{ color: theme.palette.greySecondary.main }}
                                >
                                    {value ?? "-"}
                                </Typography>
                            </Grid>
                        }
                    />
                </>
            );
        }
        if (type === "textarea") {
            return (
                <>
                    <ListItemText
                        disableTypography
                        primary={
                            <>
                                <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                                    {t(`common::label::${subtitle}`, subtitle)}
                                </Typography>
                            </>
                        }
                        secondary={
                            <Grid item sx={{ mt: 1 }}>
                                <Typography
                                    variant={"body1_400"}
                                    component={"span"}
                                    sx={{ color: theme.palette.greySecondary.main }}
                                >
                                    {value ?? "-"}
                                </Typography>
                            </Grid>
                        }
                    />
                </>
            );
        }
        if (type === "owner") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <>
                            <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                                {t(`common::label::${subtitle}`, subtitle)}
                            </Typography>
                        </>
                    }
                    secondary={
                        <>
                            <Grid
                                item
                                sx={{
                                    mt: 1,
                                }}
                            >
                                {value?.name ? (
                                    <UserDetailButton
                                        startIcon={<IconAvata size={20} htmlColor={theme.palette.greySecondary[500]} />}
                                        maxWidth={250}
                                        onClick={(e) => handleUserProfileClick(e, value.id)}
                                        userName={value.name}
                                    />
                                ) : (
                                    "-"
                                )}
                            </Grid>
                        </>
                    }
                />
            );
        }
        if (type === "date") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container alignItems={`center`} mt={1}>
                            <Box>
                                <Typography variant={"body1_400"} component={"span"}>
                                    {`${value.at ? convertUtcToLocal(value.at) : "-"} `}
                                </Typography>
                            </Box>
                            <Box>
                                {!value.hideOwner && value.by && (
                                    <>
                                        <UserDetailButton
                                            maxWidth={150}
                                            onClick={(e) => handleUserProfileClick(e, value.by.id)}
                                            userName={value.by?.name ?? value.by?.id}
                                        />
                                    </>
                                )}
                            </Box>
                        </Grid>
                    }
                />
            );
        }
        if (["categories", "tags", "attributions"].includes(type)) {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container alignItems={`center`} mt={1}>
                            {value ? (
                                <MetadataFieldReadMode
                                    type={type?.toUpperCase()}
                                    // isHidden={el?.isHidden && !el.isCheckHiddenValue}
                                    value={value}
                                />
                            ) : (
                                <Typography variant={"body1_400"} component={"span"}>
                                    -
                                </Typography>
                            )}
                        </Grid>
                    }
                />
            );
        }
        if (type === "collections") {
            return (
                <ListItemText
                    disableTypography
                    primary={
                        <Typography variant={"body3_400"} color={theme.palette.greySecondary[600]} gutterBottom>
                            {t(`common::label::${subtitle}`, subtitle)}
                        </Typography>
                    }
                    secondary={
                        <Grid container alignItems={`center`} mt={1}>
                            <Stack direction={"column"} spacing={theme.spacing(1)}>
                                {value?.length > 0 ? (
                                    <>
                                        {value?.map((item, index) => (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    width: "100%",
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <Box sx={{ flexShrink: 0, flexBasis: "20px" }}>
                                                    <CollectionTypeFlag width={20} height={16} />
                                                </Box>
                                                <Typography
                                                    variant={"body1_500"}
                                                    color={theme.palette.greySecondary.main}
                                                >
                                                    {item.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </>
                                ) : (
                                    <Typography variant={"body1_400"} component={"span"}>
                                        -
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    }
                />
            );
        }
    };

    return (
        <Box
            pt={theme.spacing(2.5)}
            pl={theme.spacing(2.5)}
            pb={theme.spacing(5)}
            pr={theme.spacing(1)}
            sx={{
                height: "100%",
                overflow: "auto",
                "& .MuiListItem-root": {
                    padding: theme.spacing(1.5),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                    },
                },
                "& .MuiListItemText-root": {
                    margin: 0,
                },
            }}
        >
            {data?.map((group, index) => (
                <div key={index}>
                    <Stack>
                        {group.attributions?.length > 0 && (
                            <Grid container>
                                <List
                                    disablePadding
                                    sx={{
                                        p: 0,
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.5,
                                    }}
                                >
                                    {group.attributions
                                        ?.filter((attribution) => !attribution.hidden)
                                        ?.map((attribution, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    pt: index > 0 ? 1 : 0,
                                                    px: 1.5,
                                                    py: 0.25,
                                                    borderRadius: 0.5,
                                                    transition: "background-color 0.3s",
                                                }}
                                            >
                                                {getComponentByType(attribution)}
                                            </ListItem>
                                        ))}
                                </List>
                            </Grid>
                        )}
                    </Stack>
                    {index < data.length - 1 && (
                        <Divider flexItem orientation={"horizontal"} sx={{ mt: 2.5, mb: 2.5 }} />
                    )}
                </div>
            ))}
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </Box>
    );
};

export default PanelLibraryOverview;
